import { MonitorActionTypes, MonitorActionUnion } from './actions';
import { PreciseDealPricingStatus, DealVersion } from '../../types/deal.types';
import { MonitorState, MonitorCardModel, MonitorMetrics } from './state';
import { MonitorCardTypes } from '../../types/monitor.types';
import { MonitorGridColumnName } from '../../grid/types/column.types';
import { SortOrder } from '../../grid/types/sorting.types';
import {
  AssignmentsActionTypes,
  AssignmentsActionsUnion
} from '../assignments/actions';
import { DealMetricsState } from '../deal-metrics/state';

export const initialMonitorState: MonitorState = {
  deals: undefined,
  cards: [
    {
      key: MonitorCardTypes.Live,
      header: 'Live',
      statuses: [PreciseDealPricingStatus.Live],
      collapsed: false,
      sortModel: [
        {
          colId: MonitorGridColumnName.Size,
          sort: SortOrder.Desc
        }
      ]
    },
    {
      key: MonitorCardTypes.Expected,
      header: 'Expected',
      statuses: [PreciseDealPricingStatus.Expected],
      collapsed: false,
      sortModel: [
        {
          colId: MonitorGridColumnName.Size,
          sort: SortOrder.Desc
        }
      ]
    },
    {
      key: MonitorCardTypes.Done,
      header: 'Sold/Priced',
      statuses: [
        PreciseDealPricingStatus.Sold,
        PreciseDealPricingStatus.Priced
      ],
      collapsed: false,
      sortModel: [
        {
          colId: MonitorGridColumnName.Size,
          sort: SortOrder.Desc
        }
      ]
    }
  ],
  metrics: {},
  loading: false,
  loaded: false,
  isActive: false,
  error: undefined
};

export function monitorReducer(
  state: MonitorState = initialMonitorState,
  action: MonitorActionUnion | AssignmentsActionsUnion
): MonitorState {
  switch (action.type) {
    case MonitorActionTypes.LoadMonitor:
      // eslint-disable-next-line no-case-declarations
      const showLoading = action.payload || state.deals === undefined;

      return {
        ...state,
        loading: showLoading
      };
    case MonitorActionTypes.LoadMonitorSuccess:
      // eslint-disable-next-line no-case-declarations
      const { data } = action.payload;

      return {
        ...state,
        deals: data,
        metrics: buildMonitorMetrics(data),
        loading: false,
        loaded: true,
        error: undefined
      };
    case MonitorActionTypes.LoadMonitorFail:
      return {
        ...state,
        deals: undefined,
        loading: false,
        loaded: false,
        error: action.payload
      };
    case MonitorActionTypes.RemoveMonitorDeals:
      return {
        ...state,
        deals:
          state.deals &&
          state.deals
            .slice(0)
            .filter((deal) => action.payload.indexOf(deal.id) === -1)
      };
    case MonitorActionTypes.TaintMonitor:
      return {
        ...state,
        deals: undefined,
        loading: false,
        loaded: false,
        error: undefined
      };
    case MonitorActionTypes.MonitorIsActive:
      return {
        ...state,
        isActive: action.payload
      };
    case AssignmentsActionTypes.AssignUsersSuccess:
      return {
        ...state,
        deals: buildAssigneUserDeals(state, action)
      };
    case MonitorActionTypes.MonitorCardToggle:
      return {
        ...state,
        cards: buildMonitorCardsToggle(state, action)
      };
    case MonitorActionTypes.MonitorSetCardSortModel:
      return {
        ...state,
        cards: buildMonitorCardsSortModel(state, action)
      };

    case MonitorActionTypes.SaveMonitorDealScore:
      return {
        ...state,
        deals: buildMonitorDealScore(state, action),
        metrics: {
          ...state.metrics,
          [action.payload.dealId]: {
            ...state.metrics[action.payload.dealId],
            saving: true,
            score: action.payload.score,
            savingError: undefined
          }
        }
      };
    case MonitorActionTypes.SaveMonitorDealScoreSuccess:
      return {
        ...state,
        deals: buildMonitorDeals(state, action)
      };
    case MonitorActionTypes.SaveMonitorDealScoreFail:
      return {
        ...state,
        deals: buildMonitorDeals(state, action),
        metrics: {
          ...state.metrics,
          [action.payload.dealId]: {
            ...state.metrics[action.payload.dealId],
            saving: false,
            score: action.payload.score,
            savingError: action.payload.error
          }
        }
      };
    case MonitorActionTypes.SaveMonitorDealRating:
      return {
        ...state,
        metrics: {
          ...state.metrics,
          [action.payload.dealId]: {
            ...state.metrics[action.payload.dealId],
            saving: true,
            rating: action.payload.rating,
            savingError: undefined
          }
        }
      };
    case MonitorActionTypes.SaveMonitorDealRatingSuccess:
      return {
        ...state,
        deals: buildMonitorDealRating(state, action)
      };
    case MonitorActionTypes.SaveMonitorDealRatingFail:
      return {
        ...state,
        metrics: {
          ...state.metrics,
          [action.payload.dealId]: {
            ...state.metrics[action.payload.dealId],
            saving: false,
            rating: undefined,
            savingError: action.payload.error
          }
        }
      };
    case MonitorActionTypes.SaveMonitorDealOwnership:
      return {
        ...state,
        metrics: {
          ...state.metrics,
          [action.payload.dealId]: {
            ...state.metrics[action.payload.dealId],
            saving: true,
            ownership: action.payload.ownership,
            savingError: undefined
          }
        }
      };
    case MonitorActionTypes.SaveMonitorDealOwnershipSuccess:
      return {
        ...state,
        deals: buildMonitorDealOwnership(state, action)
      };
    case MonitorActionTypes.SaveMonitorDealOwnershipFail:
      return {
        ...state,
        metrics: {
          ...state.metrics,
          [action.payload.dealId]: {
            ...state.metrics[action.payload.dealId],
            saving: false,
            ownership: undefined,
            savingError: action.payload.error
          }
        }
      };
    case MonitorActionTypes.SaveMonitorDealStatus:
      return {
        ...state,
        metrics: {
          ...state.metrics,
          [action.payload.dealId]: {
            ...state.metrics[action.payload.dealId],
            saving: true,
            status: action.payload.status,
            savingError: undefined
          }
        }
      };
    case MonitorActionTypes.SaveMonitorDealStatusSuccess:
      return {
        ...state,
        deals: buildMonitorDealStatus(state, action)
      };
    case MonitorActionTypes.SaveMonitorDealStatusFail:
      return {
        ...state,
        metrics: {
          ...state.metrics,
          [action.payload.dealId]: {
            ...state.metrics[action.payload.dealId],
            saving: false,
            status: undefined,
            savingError: action.payload.error
          }
        }
      };

    default:
      return state;
  }
}

export const getMonitorMetrics = (
  state: MonitorState
): MonitorMetrics<Partial<DealMetricsState>> => state.metrics;

export const getMonitorDeals = (state: MonitorState): DealVersion[] =>
  state.deals;

export const selectMonitorLoading = (state: MonitorState) => state.loading;

export const selectMonitorLoaded = (state: MonitorState) => state.loaded;

export const selectMonitorError = (state: MonitorState) => state.error;

export const selectMonitorStatus = (state: MonitorState) => state.isActive;

export const selectMonitorCardsModels = (
  state: MonitorState
): MonitorCardModel[] =>
  state.cards.map((card) => ({
    key: card.key,
    header: card.header,
    statuses: card.statuses
  }));

export const selectMonitorCardStatus = (key: MonitorCardTypes) => (
  state: MonitorState
) => {
  const card = state.cards.find((c) => c.key === key);
  return card ? card.collapsed : undefined;
};

export const selectMonitorCardSortModel = (key: MonitorCardTypes) => (
  state: MonitorState
) => {
  const card = state.cards.find((c) => c.key === key);
  return card ? card.sortModel : undefined;
};

function buildMonitorMetrics(
  deals: DealVersion[]
): MonitorMetrics<Partial<DealMetricsState>> {
  const result = {};
  if (deals.length) {
    deals.forEach((deal) => {
      if (deal && deal.metrics) {
        result[deal.id] = {
          dealId: deal.id,
          rating: deal.metrics.internalRating,
          score: deal.metrics.internalScore,
          status: deal.metrics.internalStatus,
          ownership: deal.metrics.ownership
        };
      }
    });
  }
  return result;
}

function buildMonitorDeals(state, action) {
  return state.deals.map((deal) =>
    deal.id === action.payload.dealId
      ? {
          ...deal,
          metrics: {
            ...deal.metrics,
            internalScore: action.payload.score
          }
        }
      : deal
  );
}

function buildAssigneUserDeals(state, action) {
  return (
    state.deals &&
    state.deals.map((deal: DealVersion) =>
      deal.id === action.payload.dealId
        ? {
            ...deal,
            assignedUsers: action.payload.userIds
          }
        : { ...deal }
    )
  );
}

function buildMonitorCardsToggle(state, action) {
  return state.cards.map((card) =>
    card.key === action.payload ? { ...card, collapsed: !card.collapsed } : card
  );
}

function buildMonitorCardsSortModel(state, action) {
  return state.cards.map((card) =>
    card.key === action.payload.key
      ? { ...card, sortModel: action.payload.sortModel }
      : card
  );
}

function buildMonitorDealScore(state, action) {
  return state.deals.map((deal) =>
    deal.id === action.payload.dealId && !action.payload.score
      ? {
          ...deal,
          metrics: {
            ...deal.metrics,
            internalScore: action.payload.score
          }
        }
      : deal
  );
}

function buildMonitorDealRating(state, action) {
  return state.deals.map((deal) =>
    deal.id === action.payload.dealId
      ? {
          ...deal,
          metrics: {
            ...deal.metrics,
            internalRating: action.payload.rating
          }
        }
      : deal
  );
}

function buildMonitorDealOwnership(state, action) {
  return state.deals.map((deal) =>
    deal.id === action.payload.dealId
      ? {
          ...deal,
          metrics: {
            ...deal.metrics,
            ownership: action.payload.ownership
          }
        }
      : deal
  );
}

function buildMonitorDealStatus(state, action) {
  return state.deals.map((deal) =>
    deal.id === action.payload.dealId
      ? {
          ...deal,
          metrics: {
            ...deal.metrics,
            internalStatus: action.payload.status
          }
        }
      : deal
  );
}
